import { Component, Input, Renderer2, ChangeDetectorRef } from '@angular/core';
import { aggregateBy } from '@progress/kendo-data-query';
import { GlobalsService } from '@app/cp/shared/services/globals.service';
import { BaseGridEditFormLinesComponent } from '@app/cp/shared/base-components/base-grid-edit-formlines.component';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { BaseMessageInfo } from '@app/cp/shared/models/BaseMessageInfo';
import { ConstructionService } from '@app/cp/shared/services/construction.service';
import { CommonFunctions } from '@app/cp/shared/static/common-functions';
import { MSCLFilterClass } from '@app/cp/shared/classes/filters/MSCLFilterClass';
import { DispatchBTDAdjustmentBus } from '@app/cp/shared/models/DispatchBTDAdjustmentBus';
import { FeatureFlagService } from "@app/cp/shared/services/feature-flag.service";
import { UseFeatureFlagSet, IFeatureFlagSet } from '@app/cp/shared/decorators/featureflags/featureflagset-decorator';

/*******************************************************************************************************
 * Name: DispatchCostAdjustmentsComponent
 * Creator: GRG
 * Date: 20210330
 * OverridesAndExtras:
 *      1) addModeOnly input - can be used so that grid does not show and only popup window shows
 *      2) dispatchId set input property - if set then windows shows and saves with that id
 *      3) after set edit - used because there is no editing of this so set readonly to show as view
 *      4) originalReadOnly - so can return readonly to original state if viewing from grid
 *      5) customCanSaveForm - if code is 'Other' then require note
 * Notes:
 * Modified:
 *******************************************************************************************************/
@UseFeatureFlagSet()
@Component({
    selector: 'cp-dispatch-btd-adjustments',
    templateUrl: './dispatch-btd-adjustments.component.html',
    styleUrls: ['./dispatch-btd-adjustments.component.scss']
})
export class DispatchBtdAdjustmentsComponent extends BaseGridEditFormLinesComponent<DispatchBTDAdjustmentBus, null, ConstructionService> {
    compFeatureFlagSet: IFeatureFlagSet = this as any;

    costCodeCF: MSCLFilterClass;

    @Input() addModeOnly: boolean = false; //Do not show grid -
    @Input() multiAddMode: boolean = false; //Multiple Job Adjustment -
    private _dispatchId: any;
    @Input() set dispatchId(id: any) {
        if (CommonFunctions.hasGuidValue(id)) {
            this._parentId = id;
            this._dispatchId = id;
            this.addHandler();
        }
    }
    get dispatchId() {
        return this._dispatchId;
    }
    private _jobIds: any;
    @Input() set jobIds(ids: any[]) {
        this._jobIds = ids;
        if (this.multiAddMode && ids) {
            this.createDynamicFormFields();
        }
    }
    get jobIds() {
        return this._jobIds;
    }
    private _jobNbrs: any;
    @Input() set jobNbrs(ids: any[]) {
        this._jobNbrs = ids;
    }
    get jobNbrs() {
        return this._jobNbrs;
    }
    get noteCancelButtonText(): string {
        return this.readOnly ? "Ok" : "Cancel";
    }
    afterSetEdit() {
        this.originalReadOnly = this.readOnly;
        this.readOnly = !this.isNew;
    }
    protected closeEditor(): void {
        super.closeEditor();
        this.readOnly = this.originalReadOnly;
    }
    get dispatchCostAdjustmentCodeId(): any {
        return this.hasEditForm ? this.editForm.value.dispatchCostAdjustmentCodeId : null;
    }
    originalReadOnly: boolean;
    customCanSaveForm(): boolean {
        let canSave: boolean = true;
        //if (CommonFunctions.hasGuidValue(this.dispatchCostAdjustmentCodeId) && this.dispatchCostAdjustmentCodeId === CommonValues.DispatchCostAdjustmentCode_Other) {
        //    canSave =  CommonFunctions.hasStringValue(this.editForm.value.note);
        //}
        return canSave;
    }

    constructor(globals: GlobalsService, renderer: Renderer2, cd: ChangeDetectorRef, svc_data: ConstructionService, featureFlagService: FeatureFlagService) {
        super(globals, renderer, cd, featureFlagService);
        this.svc_data = svc_data;
        this.className = "DispatchBTDAdjustmentBus";
        this.appCode = "JobBTDAdjustments";
        this.appName = "Job BTD Adjustment(s)";
        this.aggregates = [
            { field: 'amount', aggregate: 'sum' }
        ];
        this.disableCpSearchDialog();
    }
    createForm() {
        this.editForm = new UntypedFormGroup({
            'dispatchBTDAdjustmentId': new UntypedFormControl(),
            //'dispatchBTDAdjustmentCodeId': new FormControl('', Validators.required),
            'dispatchId': new UntypedFormControl(),
            'personId': new UntypedFormControl(),
            'amount': new UntypedFormControl('', Validators.required),
            'note': new UntypedFormControl(),
            'insertDt': new UntypedFormControl(),
            'insertUserId': new UntypedFormControl(),
            'updateDt': new UntypedFormControl(),
            'updateUserId': new UntypedFormControl()
        });
    }
    createDynamicFormFields() {
        if (this.jobIds && this.jobIds.length > 0) {
            this.jobIds.forEach((element, index) => {
                this.editForm.addControl('subAmount-' + index, new UntypedFormControl('', Validators.required));
            });
        }
    }
    setForm() {
        this.editForm.patchValue({
            dispatchBTDAdjustmentId: this.selectedData.dispatchBTDAdjustmentId,
            //dispatchBTDAdjustmentCodeId: this.selectedData.dispatchBTDAdjustmentCodeId,
            dispatchId: this.selectedData.dispatchId,
            personId: this.selectedData.personId,
            amount: this.selectedData.amount,
            note: this.selectedData.note,
            insertDt: this.selectedData.insertDt,
            insertUserId: this.selectedData.insertUserId,
            updateDt: this.selectedData.updateDt,
            updateUserId: this.selectedData.updateUserId
        });
    }
    protected getChangedObject(dataObj: DispatchBTDAdjustmentBus) {
        let busObj: DispatchBTDAdjustmentBus = new DispatchBTDAdjustmentBus(dataObj);
        busObj.setBeforeSave();
        return busObj;
    }
    protected save(dataObj: DispatchBTDAdjustmentBus) {
        if (this.multiAddMode) {
            this.multiSave(dataObj);
        } else {
            let busObj: DispatchBTDAdjustmentBus = this.getChangedObject(dataObj);
            var isAdded = busObj.isAdded;
            this.svc_data.saveDispatchBTDAdjustment(busObj).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    if (!this.addModeOnly) {
                        this.removeLocalAdded();
                    }
                    let newDataObj: DispatchBTDAdjustmentBus = new DispatchBTDAdjustmentBus(info.primaryReturn);
                    //if (isAdded)
                    //{
                    //    this.gridData.splice(0, 0, newDataObj);
                    //    this.cd.detectChanges();
                    //}
                    //newDataObj.visibleToCustomer = data.visibleToCustomer;

                    this.saveSuccess(newDataObj);
                    if (!this.addModeOnly) {
                        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPCOSTADJ");
                    }
                }
                else {
                    this.saveError(dataObj, "Error Saving");
                }
            });
        }
    }
    public multiSave(dataObj: DispatchBTDAdjustmentBus) {
        this.jobIds.forEach((jobId, index) => {
            dataObj.dispatchId = jobId;
            dataObj.amount = this.editForm.get([`subAmount-${index}`]).value;
            let busObj: DispatchBTDAdjustmentBus = this.getChangedObject(dataObj);
            console.log(busObj);
            var isAdded = busObj.isAdded;
            this.svc_data.saveDispatchBTDAdjustment(busObj).subscribe((info: BaseMessageInfo) => {
                if (!info.saveError) {
                    if (!this.addModeOnly) {
                        this.removeLocalAdded();
                    }
                    let newDataObj: DispatchBTDAdjustmentBus = new DispatchBTDAdjustmentBus(info.primaryReturn);
                    this.saveSuccess(newDataObj);
                    if (!this.addModeOnly) {
                        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPCOSTADJ");
                    }
                }
                else {
                    this.saveError(dataObj, "Error Saving");
                }
            });
        });
    }
    deleteIndividual(data: DispatchBTDAdjustmentBus) {
        if (data.hasId) {
            //this.svc_data.deleteNote(data.dispatchNotesId).subscribe((dataObj: BaseMessageInfo) => {
            //    if (!dataObj.saveError) {
            //        let delDataObj: v_DispatchNoteBus = new v_DispatchNoteBus(data);

            //        this.deleteSuccess(delDataObj);
            //        this.removeLocal(delDataObj);
            //        this.doParentAction(this.parentId, "GETPAGEDATA", "DISPATCHNOTES");
            //    }
            //    else {
            //        this.deleteError(data, dataObj.message);

            //    }
            //});
        }
        else {
            this.closeEditor();
        }
    }
    get canEditNote(): boolean {
        let can: boolean = true;

        //if (this.canEdit && this.selectedData) {
        //}

        return can;
    }
    processGridData() {
        this.costCodeCF = new MSCLFilterClass('dispatchCostAdjustmentCode', 'dispatchCostAdjustmentCode', this.gridData);
        if (CommonFunctions.hasArrayElementValue(this.gridData)) {
            this.aggregateResults = aggregateBy(this.gridData, this.aggregates);
        }
    }

}

